/* hgn-perf-offers.js — hgn-perf Phase 2: the Checkout step for weak baskets.
   NEW 2026-10-01 (perf-v1/PHASE2-BUILD-BRIEF.md, perf-v1/BUILD.md §9).
   Rendered by snippets/hgn-perf-offers.liquid, which theme.liquid renders
   straight after hgn-side-cart-referral, so every prototype method below
   wraps the attach and referral wrappers (outermost). Nothing in the side
   cart files changes.

   What it does
   1. Checkout tap (drawer and cart page both arrive at _proceedToCheckout):
      asks POST perf.holygrailnail.co/v1/offer (1.5 s cap). The worker
      judges the basket: STRONG → straight through, no arm. WEAK → arm A
      (straight through) or arm B (one step: 3–4 add-on picks and a
      "Continue to checkout" button styled exactly like the picks' buttons).
      Weak baskets get `_hgn_arm_offer` and `_hgn_tap` on the cart. No
      answer in time → straight through, no step, nothing changes.
   2. Once per basket (cart token); a decline never re-shows it.
   3. Accepted picks are added with plain /cart/add.js (+ `_hgn_src`).
      The add-on price is NOT a line property: the signed offers live on
      this device (48 h hold, localStorage `hgn:perf:offers`) and the ones
      in the basket are written to ONE cart attribute, `_hgn_offers`.
   4. The fold: in _render, each live offer's saving (one unit × depth %
      of the line's own price) joins _lastSavingsGbpCents /
      _lastSavingsCents / the displayed subtotal, exactly as the pair
      price does, so _mintBasketCode sends one total to the unchanged
      mint and Shopify gets one BASKET-* code. Meters and the free-gift
      orchestrator see the saving (lent like withExtraSavings).
   5. Offers restored from storage are folded only after the worker says
      their signature still checks (POST /v1/offer/verify); offers from a
      /v1/offer answer on this page are trusted as received.
   6. v2 (1 Oct): the add-on price on collection cards and the PDP (scope
      §6.3). A product this device holds a live, verified offer for shows
      its normal price struck through, the add-on price, and "Add to your
      basket for £X until <real end time>" — painted over the visible
      price only (hgn-price on the PDP, the card's price slot). JSON-LD,
      microdata, og:price and product:price are never touched; crawlers
      (by user agent) never get a paint; the paint comes off when the hold
      ends or the product is in the basket with its offer.
   7. v3 (2 Oct): the hold ends at 23:59 on its last day in the shopper's
      own time zone (the browser sends `tz`; the worker signs that second as
      the expiry, so "until Sat 3 Oct, 23:59" is the real end). In the final
      24 h the end-time line becomes a real countdown to that same signed
      expiry ("Ends in 5h 12m", "Ends in 42 min"), on the step, the cards
      and the PDP; at zero the lapse path runs. One shared timer at most,
      only while such a line is on screen, stopped while the tab is hidden.
   8. v4 (2 Oct): the Checkout-tap request carries `ship`, the side cart's own
      free-shipping line in the cart's currency, so the worker can lead with
      a pick that clears it (BUILD.md §9.17). Nothing on screen changes.
   9. v5 (3 Oct): worker .13 arms strong baskets too (OFFER_SCOPE=all, Matt:
      "enhance everyone where it's in our interest"), each segment with its
      own readout and auto-pause. Any answer with an arm is written to the
      cart and marks the basket decided, weak or strong.
  10. v6 (3 Oct): the step takes over the whole drawer (header, a grid of up
      to 8 photo cards, a pinned basket total + Continue) so nothing else
      competes with it; on the cart page it is the same grid, inline. A
      card's photo opens the quick view with the add-on price and its own
      "Add for £X". At most MAX_LIVE add-on prices fold into one basket (the
      worker's max_live, 2): the first ones to reach the basket keep them,
      and once the cap is reached the other picks, the cards and the PDP show
      normal prices. Worker .14: depths 0–50%, equal shares until 23 Nov.
  11. v7 (4 Oct, worker .15): the step leads with the "essential" for each
      range in the basket (Nail Wraps → Magic Gloss Top Coat, Gels → the LED
      Lamp, Press Ons → the Press On & Wrap Remover) — full width, at its
      normal price (never discounted), unless it is already in the basket,
      out of stock, or the signed-in customer has bought it before (the
      worker checks; the request now carries `cust`). Added with `_hgn_src`
      perf_essential:<sku> so HQ can count it. Copy: ESS_COPY, overridable
      per range through window.HGN_COPY.perf_essentials.
  12. v8 (6 Oct, the independent audit, perf-v1/AUDIT-2026-10-05.md):
      P1-1 an add-on price never folds onto the unit the referral gift is
      giving free (one saving per unit); P1-2 the per-basket cap is no
      longer read back from localStorage (the worker's answer or 2); P2-2
      the step's × goes back to the bag (the step stands down, the next
      Checkout tap goes straight through); P2-3/P2-4 the open step redraws
      on every cart change and shows normal prices while a code or Nail
      Points is in play; P2-6 no step while a KIT5 token rides the cart;
      P2-7 "decided for this basket" lasts 24 h (the cart token outlives
      emptied baskets); P3-1 an essential reads "Added" only while it is in
      the basket.
  13. v9 (8 Oct, the declutter second pass, redesigned/SIDECART-DECLUTTER-
      2026-10-07.md §6): "Continue to checkout" is the one filled button
      (Matt reversed the "styled exactly like the picks" rule; the picks'
      Add buttons stay outlined), and the essentials card is compact: the
      photo is a side thumb and the ticks are one muted line (copy in
      ESS_COPY; the pitch is unchanged). The look is the snippet's CSS.
  14. v10 (20 Oct, kit-ready, redesigned/KIT-READY-SPEC-2026-10-20.md §3):
      a basket with any kit or bundle line goes straight through without
      asking the worker. The worker never offers on one (OFFER_KIT_BASKETS
      off: no step, no arm, no tag), so the tap no longer waits up to 1.5 s
      in the kit impulse window. Opt back in with the snippet config
      `kitBaskets: true` or window.HGN_PERF_KIT_BASKETS = true, only
      together with OFFER_KIT_BASKETS=1 on the worker.
   Never: kit or bundle lines, gift lines, a line with pair / free-4th
   pricing, a Nail Points or ad-hoc-code checkout, a held POPUP code, a
   live referral claim, no analytics consent.
   Debug: window.HGN.perfOffers (state, last answer, log). */
(function () {
  'use strict';
  var B = window.__hgnSideCartBridge;
  if (!B || !B.HGNSideCart) return;
  var HGNSideCart = B.HGNSideCart;
  var P = HGNSideCart.prototype;
  if (P.__hgnPerfOffersLoaded) return;          /* load once: a double paste would fold twice */
  P.__hgnPerfOffersLoaded = true;
  var H = B.helpers || {};
  var escapeHtml = H.escapeHtml || function (s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  };
  var formatMoney = H.formatMoney || function (cents) { return '£' + (cents / 100).toFixed(2); };
  var isGiftLineItem = H.isGiftLineItem || function (it) { return !!(it && it.properties && it.properties._gift_line === '1'); };

  var CFG = window.HGN_PERF_OFFERS || {};
  var ENDPOINT = CFG.endpoint || 'https://perf.holygrailnail.co';
  var TIMEOUT_MS = CFG.timeoutMs || 1500;
  var SHIP_WINDOW = CFG.shipWindowCents || 1500;
  var K = { store: 'hgn:perf:offers', land: 'hgn:perf:land', declined: 'hgn:perf:declined', taps: 'hgn:perf:taps', on: 'hgn:perf:offers:on', trusted: 'hgn:perf:trusted', seen: 'hgn:perf:seen', max: 'hgn:perf:maxlive' };

  function lsGet(k) { try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch (e) { return null; } }
  function lsSet(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) {} }
  /* v6: add-on prices per basket (the worker's max_live, remembered). */
  /* v8 (audit P1-2): never read back from localStorage — the worker's
     max_live for this page, else the snippet's config, else 2. */
  var MAX_LIVE = Number(CFG_EARLY().maxLive) >= 1 ? Math.round(Number(CFG_EARLY().maxLive)) : 2;
  function CFG_EARLY() { return window.HGN_PERF_OFFERS || {}; }
  function ssGet(k) { try { return JSON.parse(sessionStorage.getItem(k) || 'null'); } catch (e) { return null; } }
  function ssSet(k, v) { try { sessionStorage.setItem(k, JSON.stringify(v)); } catch (e) {} }
  function cookie(name) {
    var m = new RegExp('(?:^|;\\s*)' + name + '=([^;]+)').exec(document.cookie || '');
    try { return m ? decodeURIComponent(m[1]) : ''; } catch (e) { return m ? m[1] : ''; }
  }

  /* ── The session's channel, remembered from its landing page ── */
  (function captureLanding() {
    try {
      if (ssGet(K.land)) return;
      var q = new URLSearchParams(location.search);
      var ids = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid', 'msclkid'].filter(function (k) { return q.get(k); });
      var ref = '';
      try { var r = document.referrer ? new URL(document.referrer) : null; if (r && r.hostname !== location.hostname) ref = r.hostname; } catch (e) {}
      ssSet(K.land, {
        src: q.get('utm_source') || '', med: q.get('utm_medium') || '', camp: q.get('utm_campaign') || '',
        ids: ids, ref: ref, pv2: !!q.get('pv2')
      });
    } catch (e) {}
  })();

  var on = !!CFG.on;
  try {
    var qp = new URLSearchParams(location.search).get('hgn_perf_offers');
    if (qp === '1') sessionStorage.setItem(K.on, '1');
    if (qp === '0') sessionStorage.removeItem(K.on);
    if (sessionStorage.getItem(K.on) === '1') on = true;
  } catch (e) {}
  var DBG = { state: on ? 'ready' : 'off', log: [], last: null };
  try { window.HGN = window.HGN || {}; window.HGN.perfOffers = DBG; } catch (e) {}
  function log(msg, extra) {
    DBG.log.push({ t: Date.now(), msg: msg, extra: extra || null });
    if (DBG.log.length > 60) DBG.log.shift();
    try { if (window.console && console.info) console.info('[hgn-perf-offers] ' + msg, extra ? JSON.stringify(extra) : ''); } catch (e) {}
  }
  if (!on) return;

  /* Consent — the rank snippet's check: Shopify's API when loaded, else the
     _tracking_consent cookie (purposes.a / con.CMP.a). An explicit no = no
     request, no arm, no offer. */
  function consentState() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.analyticsProcessingAllowed === 'function') return cp.analyticsProcessingAllowed() === false ? 'no' : 'yes';
    } catch (e) {}
    try {
      var c = JSON.parse(cookie('_tracking_consent') || 'null');
      var a = c && c.purposes && typeof c.purposes.a === 'boolean' ? c.purposes.a : (c && c.con && c.con.CMP ? String(c.con.CMP.a) : null);
      if (a === false || a === '0') return 'no';
      if (a === true || a === '1') return 'yes';
    } catch (e) {}
    return 'unknown';
  }
  function deviceId() {
    var d = null;
    try { d = localStorage.getItem('hgn:did'); } catch (e) {}
    return d || (CFG.customer ? 'cust-' + CFG.customer : '');
  }

  /* ── Line helpers (the attach snippet's definitions) ── */
  function prop(it, k) { var p = it && it.properties; return p && p[k] != null ? String(p[k]) : ''; }
  function isBundleLine(it) { return !!(prop(it, '_bundle_id') || prop(it, '_box_id') || prop(it, 'Box ID')); }
  function offerKind(it) { return prop(it, '_hgn_offer_kind'); }
  function unitRetail(it) {
    if (it.original_price != null) return it.original_price;
    var t = it.original_line_price != null ? it.original_line_price : (it.line_price || 0);
    return Math.round(t / Math.max(1, it.quantity || 1));
  }
  /* Nail Points and ad-hoc codes take their own checkout paths and mint
     none of our savings (the same two signals as the pair price). */
  function suspended(cart) {
    var a = (cart && cart.attributes) || {};
    var v = a._hgn_discount_intent;
    if (v === 'nailpoints' || v === 'adhoc') return true;
    return ((cart && cart.discount_codes) || []).some(function (d) { return d && d.code && /^(NAILPOINTS|BEANS)-/i.test(d.code); });
  }
  function savingFor(unitPence, depth) { return Math.round((unitPence * depth) / 100); }

  /* ── Stored offers (the 48 h hold) and which of them this page trusts ──
     Trusted = arrived in a /v1/offer answer, or /v1/offer/verify said its
     signature checks. Kept for the tab (sessionStorage) as the exact
     signed fields, so a stored offer edited afterwards is never trusted. */
  var SIGNED = ['id', 'did', 'm', 'p', 'v', 'h', 'r', 'd', 'cur', 'pres', 'g', 'exp', 'sig'];
  function signedPart(o) { var x = {}; SIGNED.forEach(function (k) { x[k] = o[k]; }); return x; }
  var TRUSTED = ssGet(K.trusted) || {};
  function trust(o) {
    if (!o || !o.id) return;
    TRUSTED[o.id] = JSON.stringify(signedPart(o));
    var keep = {}, now = Math.floor(Date.now() / 1000);
    Object.keys(TRUSTED).forEach(function (id) { try { if (JSON.parse(TRUSTED[id]).exp > now) keep[id] = TRUSTED[id]; } catch (e) {} });
    TRUSTED = keep;
    ssSet(K.trusted, TRUSTED);
  }
  function isTrusted(o) { return !!(o && o.id && TRUSTED[o.id] && TRUSTED[o.id] === JSON.stringify(signedPart(o))); }
  function nowSec() { return Math.floor(Date.now() / 1000); }
  function readOffers() {
    var s = lsGet(K.store);
    var list = (s && Array.isArray(s.offers)) ? s.offers : [];
    return list.filter(function (o) { return o && o.id && o.exp > nowSec(); });
  }
  function storeOffers(list) {
    var byP = {};
    readOffers().concat(list || []).forEach(function (o) { if (o && o.p) byP[o.p] = o; });
    var out = Object.keys(byP).map(function (k) { return byP[k]; }).sort(function (a, b) { return b.exp - a.exp; }).slice(0, 12);
    lsSet(K.store, { offers: out });
  }
  function declined() { return ssGet(K.declined) || []; }
  function addDeclined(handles) {
    var d = declined();
    (handles || []).forEach(function (h) { h = String(h || '').toLowerCase(); if (h && d.indexOf(h) < 0) d.push(h); });
    ssSet(K.declined, d.slice(-20));
  }
  /* The IR-1 pair card's handles: a declined (or skipped) pair design is
     never offered again by the step. */
  var pairShown = [];
  document.addEventListener('hgn:offer:shown', function (e) {
    var d = (e && e.detail) || {};
    pairShown = [d.handle, d.freeHandle].filter(Boolean);
  });
  document.addEventListener('hgn:offer:declined', function () { addDeclined(pairShown); });

  /* Offers on this basket's lines: trusted, unexpired, in the cart's
     currency, one per plain line (never kit, gift, pair or free-4th). */
  function liveOffers(cart) {
    if (!cart || !cart.items || !cart.items.length || suspended(cart)) return [];
    var used = {}, out = [], now = nowSec();
    /* v8 (audit P1-1): the referral gift gives one set unit away free; an
       add-on price never folds onto that same unit. A line of 2+ still has
       a unit left for the add-on price. */
    var refKey = null;
    try {
      var rg = mounted && typeof mounted._referralGift === 'function' ? mounted._referralGift() : null;
      if (rg && rg.item && rg.pres > 0 && (rg.item.quantity || 1) <= 1) refKey = rg.item.key;
    } catch (e) {}
    readOffers().forEach(function (o) {
      if (!(o.d > 0) || !isTrusted(o) || o.exp <= now || o.cur !== cart.currency) return;
      for (var i = 0; i < cart.items.length; i++) {
        var it = cart.items[i];
        if (used[i] || String(it.variant_id) !== String(o.v)) continue;
        if (isGiftLineItem(it) || isBundleLine(it) || offerKind(it)) continue;
        if (refKey && it.key === refKey) continue;
        used[i] = true;
        var sp = savingFor(unitRetail(it), o.d);
        var sg = cart.currency === 'GBP' ? sp : savingFor(o.g, o.d);
        out.push({ o: o, it: it, index: i, savePres: sp, saveGbp: sg });
        break;
      }
    });
    /* v6: the first MAX_LIVE add-on prices to reach this basket keep them
       (first seen on a line, remembered on this device). */
    var seen = lsGet(K.seen) || {}, changed = false, t = Date.now();
    out.forEach(function (x, k) { if (!seen[x.o.id]) { seen[x.o.id] = t + k; changed = true; } });
    if (changed) {
      var keep = {}, ids = {};
      readOffers().forEach(function (o) { ids[o.id] = true; });
      Object.keys(seen).forEach(function (id) { if (ids[id]) keep[id] = seen[id]; });
      lsSet(K.seen, keep);
    }
    out.sort(function (a, b) { return seen[a.o.id] - seen[b.o.id]; });
    return out.slice(0, MAX_LIVE);
  }
  function capReached(cart) { return liveOffers(cart).length >= MAX_LIVE; }
  function offersPres(cart) { return liveOffers(cart).reduce(function (s, x) { return s + x.savePres; }, 0); }

  /* Restored offers are checked with the worker before they count (the
     fold) or are painted (cards, PDP). One call per page per set;
     failures simply don't fold or paint. */
  var verifyP = null, verifiedFor = '';
  function needVerify(cart) {
    if (!cart || !cart.items) return [];
    var vids = {};
    cart.items.forEach(function (it) { vids[String(it.variant_id)] = true; });
    return readOffers().filter(function (o) { return !isTrusted(o) && vids[String(o.v)]; });
  }
  function verifyList(list) {
    if (!list.length) return Promise.resolve(false);
    var key = list.map(function (o) { return o.id; }).join(',');
    if (verifyP && verifiedFor === key) return verifyP;
    verifiedFor = key;
    var sent = {};
    list.forEach(function (o) { sent[o.id] = o; });
    verifyP = fetchJson(ENDPOINT + '/v1/offer/verify', { did: deviceId(), offers: list.map(signedPart) }, 2500).then(function (r) {
      if (!r || !r.ok) { log('verify: no answer'); return false; }
      (r.valid || []).forEach(function (id) { trust(sent[id]); });
      var bad = {};
      (r.invalid || []).forEach(function (x) { bad[x.id] = x.why; });
      if (Object.keys(bad).length) {
        log('verify: refused', bad);
        lsSet(K.store, { offers: readOffers().filter(function (o) { return !bad[o.id]; }) });
      }
      return (r.valid || []).length > 0;
    });
    return verifyP;
  }
  function verifyStored(self) { return verifyList(needVerify(self.cart)); }

  function fetchJson(url, body, timeoutMs) {
    var ctl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = setTimeout(function () { if (ctl) ctl.abort(); }, timeoutMs);
    return Promise.race([
      fetch(url, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, credentials: 'omit', body: JSON.stringify(body), signal: ctl ? ctl.signal : undefined })
        .then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; }),
      new Promise(function (res) { setTimeout(function () { res(null); }, timeoutMs + 50); })
    ]).then(function (x) { clearTimeout(timer); return x; });
  }
  function ev(kind, tap, offer, surface) {
    try {
      var b = JSON.stringify({ consent: true, did: deviceId(), tap: tap, kind: kind, offer: offer || null, surface: surface });
      if (navigator.sendBeacon) navigator.sendBeacon(ENDPOINT + '/v1/offer/ev', new Blob([b], { type: 'text/plain' }));
      else fetch(ENDPOINT + '/v1/offer/ev', { method: 'POST', body: b, headers: { 'Content-Type': 'text/plain' }, credentials: 'omit', keepalive: true }).catch(function () {});
    } catch (e) {}
  }
  function cartUpdate(attrs) {
    return fetch('/cart/update.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).then(function (r) { return r.ok; }).catch(function () { return false; });
  }
  function bounded(p, ms) { return Promise.race([p, new Promise(function (res) { setTimeout(res, ms); })]); }

  /* ── `_hgn_offers`: the live offers, one cart attribute ── */
  var attrInflight = null;
  function syncOffersAttr(self) {
    var cart = self.cart;
    if (!cart || !cart.token) return Promise.resolve();
    var live = liveOffers(cart).map(function (x) { return signedPart(x.o); });
    var want = live.length ? JSON.stringify(live) : '';
    var have = (cart.attributes && cart.attributes._hgn_offers) || '';
    if (want === have || self._perfAttrWant === want) return attrInflight || Promise.resolve();
    self._perfAttrWant = want;
    attrInflight = cartUpdate({ _hgn_offers: want }).then(function (ok) {
      if (!ok) self._perfAttrWant = null;
      else if (self.cart && self.cart.attributes) self.cart.attributes._hgn_offers = want;
      attrInflight = null;
    });
    return attrInflight;
  }

  /* ── The fold (outermost _render wrapper) ── */
  HGNSideCart.prototype._perfApplyFold = function () {
    var cart = this.cart;
    var live = liveOffers(cart);
    this._perfLive = live;
    var g = 0, p = 0;
    live.forEach(function (x) { g += x.saveGbp; p += x.savePres; });
    if (!cart || !cart.item_count || p <= 0) return;
    this._lastSavingsGbpCents = (this._lastSavingsGbpCents || 0) + g;
    this._lastSavingsCents = (this._lastSavingsCents || 0) + p;
    this._perfFold = { gbp: g, pres: p, gbpAfter: this._lastSavingsGbpCents, presAfter: this._lastSavingsCents, subAfter: null };
    var cur = cart.currency;
    if (this.savingsEl && this.savingsValueEl) {
      /* _lastSavingsCents excludes the KIT5 fold (painted separately by the
         base render) — re-add it, as the pair and referral folds do. */
      var k5 = 0;
      try { if (typeof this._kit5FoldGbpCents === 'function' && typeof H.gbpToActiveCents === 'function') k5 = H.gbpToActiveCents((this._kit5FoldGbpCents() || 0) / 100); } catch (e) {}
      this.savingsValueEl.textContent = formatMoney(this._lastSavingsCents + k5, cur);
      this.savingsEl.hidden = false;
    }
    if (typeof this._lastDisplayedSubtotalCents === 'number') {
      this._lastDisplayedSubtotalCents = Math.max(0, this._lastDisplayedSubtotalCents - p);
      this._perfFold.subAfter = this._lastDisplayedSubtotalCents;
      if (this.subtotalEl) this.subtotalEl.textContent = formatMoney(this._lastDisplayedSubtotalCents, cur);
    }
    var self = this;
    live.forEach(function (x) {
      if (!self.itemsEl || x.savePres <= 0) return;
      var idx = x.it._lineIndex != null ? x.it._lineIndex : x.index + 1;
      var btn = self.itemsEl.querySelector('[data-hgn-side-cart-action="remove"][data-hgn-side-cart-line="' + idx + '"]');
      var row = btn && btn.closest ? btn.closest('.hgn-side-cart__item') : null;
      var priceEl = row ? row.querySelector('.hgn-side-cart__item-price') : null;
      if (!priceEl || priceEl.getAttribute('data-hgn-perf-done')) return;
      var retail = x.it.original_line_price != null ? x.it.original_line_price : (x.it.line_price || 0);
      priceEl.innerHTML = '<s class="hgn-side-cart__item-price--strike">' + formatMoney(retail, cur) + '</s> ' +
        formatMoney(Math.max(0, retail - x.savePres), cur) + ' <span class="hgn-side-cart__item-pair">Add-on price</span>';
      priceEl.setAttribute('data-hgn-perf-done', '1');
    });
  };
  /* An offer that ran out while its line sat in the basket: tell the
     shopper, and keep telling them on every render (other drawer code
     clears the note row) until that line leaves the basket. */
  var lapsed = ssGet('hgn:perf:lapsed') || {};
  HGNSideCart.prototype._perfNoteLapsed = function () {
    var cart = this.cart;
    if (!cart || !cart.items || !this._setCheckoutNote || this._checkoutMintFailed) return;
    var s = lsGet(K.store);
    var all = (s && Array.isArray(s.offers)) ? s.offers : [];
    var now = nowSec();
    var vids = {};
    cart.items.forEach(function (it) { if (!isBundleLine(it) && !isGiftLineItem(it) && !offerKind(it)) vids[String(it.variant_id)] = true; });
    var changed = false;
    all.forEach(function (o) {
      if (o && o.exp <= now && o.d > 0 && vids[String(o.v)] && !lapsed[o.v]) {
        lapsed[o.v] = 'Your add-on price on ' + escapeHtml(displayTitle(o.t) || 'one item') + ' ended at ' + escapeHtml(endTime(o.exp)) + ', so it\u2019s back at its normal price.';
        changed = true;
      }
    });
    if (all.some(function (o) { return o && o.exp <= now; })) lsSet(K.store, { offers: all.filter(function (o) { return o && o.exp > now; }) });
    Object.keys(lapsed).forEach(function (v) { if (!vids[v]) { delete lapsed[v]; changed = true; } });
    if (changed) ssSet('hgn:perf:lapsed', lapsed);
    var msgs = Object.keys(lapsed).map(function (v) { return lapsed[v]; });
    if (msgs.length && this.checkoutNoteEl && this.checkoutNoteEl.innerHTML.indexOf(msgs[0]) < 0) this._setCheckoutNote(msgs.join(' '));
  };

  var mounted = null;
  var origRender = P._render;
  HGNSideCart.prototype._render = function () {
    /* Back out the previous fold before the base render (and the pair and
       referral folds) run, so their own back-outs still see the values
       they left. */
    var f = this._perfFold;
    if (f) {
      if (this._lastSavingsGbpCents === f.gbpAfter) this._lastSavingsGbpCents -= f.gbp;
      if (this._lastSavingsCents === f.presAfter) this._lastSavingsCents -= f.pres;
      if (f.subAfter != null && this._lastDisplayedSubtotalCents === f.subAfter) this._lastDisplayedSubtotalCents += f.pres;
      this._perfFold = null;
    }
    mounted = this;
    var out = origRender.apply(this, arguments);
    if (this._perfStep && this._perfStep.open) this._perfStep.dirty = true;   /* v8 (audit P2-3) */
    try {
      this._perfApplyFold();
      this._perfNoteLapsed();
      this._perfReassertStep();
      syncCartPage(this);
      syncOffersAttr(this);
      schedulePaint();
      var self = this;
      if (needVerify(this.cart).length) verifyStored(this).then(function (any) { if (any && self.cart) self._render(); });
    } catch (e) { log('fold error: ' + (e && e.message)); }
    return out;
  };

  /* Meters, the free-gift orchestrator and the attach context must see the
     discounted subtotal Shopify will rate shipping on (the pair price's
     withExtraSavings pattern). */
  function lend(self, fn, args) {
    if (self._perfLending) return fn.apply(self, args);   /* nested call: already lent */
    var extra = offersPres(self.cart) || 0;
    if (!extra) return fn.apply(self, args);
    var prev = self._lastAdHocSavingsCents || 0;
    self._lastAdHocSavingsCents = prev + extra;
    self._perfLending = true;
    try { return fn.apply(self, args); } finally { self._lastAdHocSavingsCents = prev; self._perfLending = false; }
  }
  ['_renderProgress', '_orchestrateFreeGift', '_attachContext'].forEach(function (m) {
    var orig = P[m];
    if (typeof orig !== 'function') return;
    HGNSideCart.prototype[m] = function () { return lend(this, orig, arguments); };
  });

  /* ── The cart page mirrors the drawer's figures while an offer is live ── */
  function syncCartPage(self) {
    var sec = document.querySelector('[data-hgn-cart-page]');
    if (!sec || !self.cart) return;
    var live = self._perfLive || [];
    if (!live.length) return;
    var cur = self.cart.currency;
    var setText = function (el, t) { if (el && el.textContent !== t) el.textContent = t; };
    var sub = sec.querySelector('[data-hgn-summary-subtotal]');
    if (typeof self._lastDisplayedSubtotalCents === 'number') setText(sub, formatMoney(self._lastDisplayedSubtotalCents, cur));
    var row = sec.querySelector('[data-hgn-summary-savings]');
    var val = sec.querySelector('[data-hgn-summary-savings-value]');
    if (row && val) {
      var k5 = 0;
      try { if (typeof self._kit5FoldGbpCents === 'function' && typeof H.gbpToActiveCents === 'function') k5 = H.gbpToActiveCents((self._kit5FoldGbpCents() || 0) / 100); } catch (e) {}
      setText(val, formatMoney((self._lastSavingsCents || 0) + k5, cur));
      if (row.hidden) row.hidden = false;
    }
    live.forEach(function (x) {
      var line = sec.querySelector('[data-hgn-cart-page-line][data-line-key="' + String(x.it.key).replace(/"/g, '') + '"]');
      var pe = line ? line.querySelector('.hgn-cart-page__line-price') : null;
      if (!pe || pe.getAttribute('data-hgn-perf-done') || x.savePres <= 0) return;
      var retail = x.it.original_line_price != null ? x.it.original_line_price : (x.it.line_price || 0);
      pe.innerHTML = '<span class="hgn-cart-page__line-price-strike">' + formatMoney(retail, cur) + '</span> ' + formatMoney(Math.max(0, retail - x.savePres), cur);
      pe.setAttribute('data-hgn-perf-done', '1');
    });
  }
  /* The cart page swaps its whole section after every cart change: put our
     figures (and an open step) back each time it does. */
  function watchCartPage() {
    var sec = document.querySelector('[data-hgn-cart-page]');
    if (!sec || !sec.parentNode || typeof MutationObserver !== 'function') return;
    var busy = false;
    new MutationObserver(function () {
      if (!mounted || busy) return;
      busy = true;
      try {
        syncCartPage(mounted);
        if (mounted._perfStep && mounted._perfStep.surface === 'cart') mounted._perfReassertStep();
      } finally { setTimeout(function () { busy = false; }, 0); }
    }).observe(sec.parentNode, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchCartPage); else watchCartPage();

  /* ── The step ── */
  function endTime(exp) {
    try {
      return new Date(exp * 1000).toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch (e) { return new Date(exp * 1000).toISOString().slice(0, 16).replace('T', ' '); }
  }
  /* The real countdown (v3). More than 24 h left: the date line. The final
     24 h: minutes to the signed expiry, no seconds. Never a fake clock: the
     same exp the worker signed and the drawer folds by. */
  var DAY_S = 86400;
  function countdown(exp) {
    var left = exp - Date.now() / 1000;
    if (left > DAY_S || left <= 0) return '';
    var mins = Math.floor(left / 60);
    if (mins < 1) return 'Ends in under 1 min';
    if (mins < 60) return 'Ends in ' + mins + ' min';
    return 'Ends in ' + Math.floor(mins / 60) + 'h ' + (mins % 60) + 'm';
  }
  /* kind: 'step' | 'card' | 'pdp'; lead: "Add to your basket for £X" */
  function holdText(kind, lead, exp) {
    var c = countdown(exp);
    if (kind === 'step') return c ? 'Add-on prices are held for you. ' + c + '.' : 'Add-on prices are held for you until ' + endTime(exp) + '.';
    return lead + (c ? '. ' + c : ' until ' + endTime(exp)) + (kind === 'pdp' ? '.' : '');
  }
  function holdAttrs(kind, lead, exp) {
    return ' data-hgn-perf-exp="' + Number(exp) + '" data-hgn-perf-kind="' + kind + '"' + (lead ? ' data-hgn-perf-lead="' + escapeHtml(lead) + '"' : '');
  }
  function displayTitle(t) {
    return String(t || '')
      .replace(/\s*[-–—]\s*holy grail nail\s*$/i, '')
      .replace(/^\s*holy grail nail\s*[-–—]?\s*/i, '')
      .replace(/\s*[-–—]\s*holy (classics|gels)\b.*$/i, '')
      .replace(/\s*[-–—]\s*(non-uv\s+)?nail wraps\s*$/i, '')
      .trim() || String(t || '');
  }
  /* v7: the essentials' copy, from the product listings (Matt, 4 Oct: "make
     sure they are as attractive as possible"). Range names in title case.
     v9 (8 Oct): the points print as ONE muted line joined with " · ", so two
     are trimmed by words only (Matt approved the card compromise): "Protects
     your wraps from hand cream, soap and sanitiser" → "Protects from soap and
     sanitiser"; "USB powered, so it travels with you" → "USB powered, travels
     with you". */
  var ESS_COPY = {
    classic: {
      eyebrow: 'Made for your Nail Wraps',
      body: 'We only recommend Magic Gloss Top Coat with our Nail Wraps. For extra shine, strength and longevity, we’d recommend adding it to your basket.',
      points: ['Touch dry in 30 seconds', 'No UV lamp needed', 'Protects from soap and sanitiser']
    },
    gels: {
      eyebrow: 'Needed for your Gels',
      body: 'Your Gels set hard and glossy under an LED lamp, and ours is made for them: six powerful LEDs cure each coat in 60–120 seconds with the built-in timer.',
      points: ['Cures in 60–120 seconds', 'USB powered, travels with you', 'Designed for our Gel Nail Wraps']
    },
    'press-ons': {
      eyebrow: 'Made for your Press Ons',
      body: 'Take your Press Ons off the easy way. Our remover gently dissolves the glue so they lift off in minutes: no picking, no acetone, no damage to your natural nails.',
      points: ['Acetone-free and odourless', 'Gentle, non-drying formula', 'Vegan and cruelty-free']
    }
  };
  function essCopy(range) {
    var o = {};
    try { o = ((window.HGN_COPY || {}).perf_essentials || {})[range] || {}; } catch (e) {}
    var b = ESS_COPY[range] || {};
    return { eyebrow: o.eyebrow || b.eyebrow || '', body: o.body || b.body || '', points: o.points || b.points || [] };
  }
  function essentialsHtml(self, st) {
    var cur = self.cart.currency;
    return (st.essentials || []).map(function (e) {
      var inBasket = (self.cart.items || []).some(function (it) { return String(it.variant_id) === String(e.v); });
      if (st.essAdded[e.p] === true && !inBasket) delete st.essAdded[e.p];   /* v8 (audit P3-1) */
      var c = essCopy(e.r), added = st.essAdded[e.p] || (inBasket ? true : undefined);
      return '<div class="hgn-perf-step__ess">' +
        '<button type="button" class="hgn-perf-step__ess-media" data-hgn-perf-qv-ess="' + escapeHtml(e.p) + '" aria-label="' + escapeHtml('See more of ' + displayTitle(e.t)) + '">' +
          (e.i ? '<img src="' + escapeHtml(e.i) + '" alt="" width="300" height="300" loading="eager">' : '') + '</button>' +
        '<div class="hgn-perf-step__ess-body">' +
          '<p class="hgn-perf-step__ess-eyebrow">' + escapeHtml(c.eyebrow) + '</p>' +
          '<p class="hgn-perf-step__ess-name">' + escapeHtml(displayTitle(e.t)) + '</p>' +
          '<p class="hgn-perf-step__ess-text">' + escapeHtml(c.body) + '</p>' +
          /* v9 (8 Oct): the ticks as one short muted line, beside a compact Add
             (the photo is a side thumb; snippet CSS) */
          '<div class="hgn-perf-step__ess-row">' +
            (c.points.length ? '<p class="hgn-perf-step__ess-points">' + escapeHtml(c.points.join(' · ')) + '</p>' : '') +
            '<button type="button" class="hgn-perf-step__btn hgn-perf-step__ess-add" data-hgn-perf-ess-add="' + escapeHtml(e.p) + '"' + (added ? ' disabled' : '') + '>' +
              (added === 'busy' ? 'Adding…' : added ? 'Added' : 'Add for ' + formatMoney(e.pres, cur)) + '</button>' +
          '</div>' +
        '</div>' +
      '</div>';
    }).join('');
  }
  function stepHtml(self, st) {
    var cart = self.cart, cur = cart.currency;
    var ship = '';
    try {
      var ctx = self._attachContext && self._attachContext();
      if (ctx && !ctx.shipUnlocked && ctx.shipCents) {
        var gap = ctx.shipCents - ctx.subtotalCents;
        if (gap > 0 && gap <= SHIP_WINDOW) ship = '<p class="hgn-perf-step__ship">You’re <strong>' + formatMoney(gap, cur) + '</strong> from free shipping.</p>';
      }
    } catch (e) {}
    /* v6: which add-on prices this basket holds, and whether it has its
       MAX_LIVE; a pick keeps its add-on price only if it is one of them or
       the basket still has room. */
    var live = liveOffers(cart), liveIds = {};
    live.forEach(function (x) { liveIds[x.o.id] = true; });
    var full = live.length >= MAX_LIVE;
    var paused = suspended(cart);   /* v8 (audit P2-4): a code or Nail Points in play — no add-on prices */
    /* the note names the EARLIEST end among the add-on prices on show, so it
       is true of every one of them */
    var endExp = 0, now = nowSec(), hiddenDeals = 0;
    var cards = st.offers.map(function (o) {
      var added = st.added[o.id];
      var deal = o.d > 0 && o.exp > now && !paused && (!full || liveIds[o.id]);
      if (o.d > 0 && o.exp > now && !deal) hiddenDeals++;
      if (deal && (!endExp || o.exp < endExp)) endExp = o.exp;
      var name = displayTitle(o.t);
      var price = deal
        ? '<s>' + formatMoney(o.pres, cur) + '</s> <strong>' + formatMoney(o.price, cur) + '</strong>'
        : '<strong>' + formatMoney(o.pres, cur) + '</strong>';
      return '<li class="hgn-perf-step__card">' +
        '<button type="button" class="hgn-perf-step__media" data-hgn-perf-qv="' + escapeHtml(o.id) + '" aria-label="' + escapeHtml('See more of ' + name) + '">' +
          (o.i ? '<img src="' + escapeHtml(o.i) + '" alt="" width="300" height="300" loading="lazy">' : '') +
          (deal ? '<span class="hgn-perf-step__badge">−' + Number(o.d) + '%</span>' : '') +
        '</button>' +
        '<span class="hgn-perf-step__name">' + escapeHtml(name) + '</span>' +
        '<span class="hgn-perf-step__price">' + price + '</span>' +
        '<button type="button" class="hgn-perf-step__btn" data-hgn-perf-add="' + escapeHtml(o.id) + '"' + (added ? ' disabled' : '') + '>' +
          (added === 'busy' ? 'Adding…' : added ? 'Added' : 'Add for ' + formatMoney(deal ? o.price : o.pres, cur)) + '</button>' +
      '</li>';
    }).join('');
    var capNote = full && hiddenDeals ? '<p class="hgn-perf-step__cap">You’ve got your ' + MAX_LIVE + ' add-on prices. The rest are at their normal price.</p>' : '';
    var total = typeof self._lastDisplayedSubtotalCents === 'number' ? self._lastDisplayedSubtotalCents : cart.items_subtotal_price;
    var drawer = st.surface !== 'cart';
    return '<div class="hgn-perf-step__head">' +
        (drawer ? '<button type="button" class="hgn-perf-step__x" data-hgn-perf-x aria-label="Back to your bag">×</button>' : '') +
        '<p class="hgn-perf-step__eyebrow">Before you check out</p>' +
        '<p class="hgn-perf-step__title" tabindex="-1">Why not try one of these?</p>' + ship + capNote +
        (endExp ? '<p class="hgn-perf-step__note"' + holdAttrs('step', '', endExp) + '>' + escapeHtml(holdText('step', '', endExp)) + '</p>' : '') +
      '</div>' +
      '<div class="hgn-perf-step__scroll" data-hgn-perf-scroll>' + essentialsHtml(self, st) + (cards ? '<ul class="hgn-perf-step__grid">' + cards + '</ul>' : '') + '</div>' +
      '<div class="hgn-perf-step__foot">' +
        '<p class="hgn-perf-step__total"><span>Your basket</span><strong>' + formatMoney(total, cur) + '</strong></p>' +
        '<button type="button" class="hgn-perf-step__btn hgn-perf-step__continue" data-hgn-perf-continue>Continue to checkout</button>' +
      '</div>';
  }
  function hostFor(self, surface) {
    if (surface === 'cart') {
      var btn = document.querySelector('[data-hgn-cart-page-checkout]');
      return btn ? { parent: btn.parentNode, before: btn, hide: btn } : null;
    }
    /* v6: the drawer — the step covers the whole panel */
    return self.panel ? { parent: self.panel, before: null, hide: null, takeover: true } : null;
  }
  HGNSideCart.prototype._perfReassertStep = function () {
    var st = this._perfStep;
    if (!st || !st.open) return;
    var h = hostFor(this, st.surface);
    if (!h) return;
    if (!st.el || !st.el.isConnected || st.el.parentNode !== h.parent || (h.before && st.el.nextSibling !== h.before)) {
      if (!st.el) {
        st.el = document.createElement('div');
        st.el.className = 'hgn-perf-step' + (st.surface === 'cart' ? ' hgn-perf-step--page' : '');
        st.el.setAttribute('role', 'group');
        st.el.setAttribute('aria-label', 'Before you check out');
        st.el.setAttribute('data-hgn-perf-step', '');
        bindStep(this, st);
      }
      h.parent.insertBefore(st.el, h.before);
      if (h.takeover) {
        h.parent.setAttribute('data-hgn-perf-takeover', '');
        st.focus = true;
      }
    }
    if (st.dirty || !st.el.innerHTML) {
      var sc = st.el.querySelector('[data-hgn-perf-scroll]');
      var top = sc ? sc.scrollTop : 0;
      st.el.innerHTML = stepHtml(this, st);
      st.dirty = false;
      var sc2 = st.el.querySelector('[data-hgn-perf-scroll]');
      if (sc2 && top) sc2.scrollTop = top;
      kick();
    }
    if (st.focus) {
      st.focus = false;
      var t = st.el.querySelector('.hgn-perf-step__title');
      if (t) { try { t.focus({ preventScroll: true }); } catch (e) {} }
    }
    if (h.hide) { h.hide.hidden = true; st.hidden = h.hide; }
    if (this._pairOfferOpen && typeof this._hidePairOffer === 'function') this._hidePairOffer();
  };
  function closeStep(self) {
    var st = self._perfStep;
    if (!st) return;
    st.open = false;
    if (st.el && st.el.parentNode) st.el.parentNode.removeChild(st.el);
    if (self.panel) self.panel.removeAttribute('data-hgn-perf-takeover');
    var h = hostFor(self, st.surface);
    if (h && h.hide) h.hide.hidden = false;
    if (st.hidden) st.hidden.hidden = false;
    self._perfStep = null;
  }
  /* An add that lifts the basket over the free-gift threshold makes the
     orchestrator add the gift line asynchronously; the mint must wait for it
     or the gift is charged at checkout (the IR-1 gift race, 9 Sep). */
  function settleGift(self) {
    var t0 = Date.now();
    return new Promise(function (res) {
      (function wait() {
        if (!self._giftInflight || Date.now() - t0 > 4000) return res();
        setTimeout(wait, 150);
      })();
    }).then(function () { return self.refresh ? self.refresh() : null; }).catch(function () {});
  }
  function bindStep(self, st) {
    st.el.addEventListener('click', function (e) {
      var add = e.target.closest && e.target.closest('[data-hgn-perf-add]');
      var cont = e.target.closest && e.target.closest('[data-hgn-perf-continue]');
      if (cont) {
        if (st.going) return;
        st.going = true;
        cont.disabled = true;
        cont.textContent = 'Taking you to checkout…';
        ev('continue', st.tap, null, st.surface);
        var declinedHandles = st.offers.filter(function (o) { return !st.added[o.id]; }).map(function (o) { return o.h; });
        addDeclined(declinedHandles);
        Promise.resolve(st.pending).then(function () { return self.refresh ? self.refresh() : null; }).catch(function () {})
          .then(function () { return settleGift(self); })
          .then(function () { return bounded(syncOffersAttr(self), 2000); })
          .then(function () {
            closeStep(self);
            st.proceed();
          });
        return;
      }
      /* v8 (audit P2-2): × goes back to the bag — the step stands down (the
         basket is already decided, so the next Checkout tap goes straight
         through) and the drawer stays open on the shopper's lines. */
      if (e.target.closest && e.target.closest('[data-hgn-perf-x]')) {
        addDeclined(st.offers.filter(function (o) { return !st.added[o.id]; }).map(function (o) { return o.h; }));
        try { if (typeof window.clarity === 'function') window.clarity('set', 'hgn_perf_step', 'back'); } catch (x) {}
        closeStep(self);
        if (self._render) self._render();
        return;
      }
      var essAdd = e.target.closest && e.target.closest('[data-hgn-perf-ess-add]');
      if (essAdd) { if (!essAdd.disabled) addEssential(self, st, essAdd.getAttribute('data-hgn-perf-ess-add')); return; }
      var essQv = e.target.closest && e.target.closest('[data-hgn-perf-qv-ess]');
      if (essQv) { openEssentialView(self, st, essQv.getAttribute('data-hgn-perf-qv-ess')); return; }
      var qvBtn = e.target.closest && e.target.closest('[data-hgn-perf-qv]');
      if (qvBtn) { openQuickView(self, st, qvBtn.getAttribute('data-hgn-perf-qv')); return; }
      if (!add || add.disabled) return;
      addPick(self, st, add.getAttribute('data-hgn-perf-add'));
    });
  }
  /* v6: a pick's photo opens the theme's quick view (every photo, the
     description) with this pick's add-on price and its own Add button. */
  function openQuickView(self, st, id) {
    var o = null;
    st.offers.forEach(function (x) { if (x.id === id) o = x; });
    var qv = window.HGN && window.HGN.quickView;
    if (!o || !o.h || !qv || typeof qv.open !== 'function') return;
    var cur = self.cart.currency, now = nowSec();
    var liveIds = {};
    liveOffers(self.cart).forEach(function (x) { liveIds[x.o.id] = true; });
    var deal = o.d > 0 && o.exp > now && (!capReached(self.cart) || liveIds[o.id]);
    var priceNow = deal ? o.price : o.pres;
    var opts = { context: 'perf-step' };
    if (deal) opts.price = { text: formatMoney(o.price, cur), was: formatMoney(o.pres, cur), note: holdText('pdp', leadText(o.price, cur), o.exp) };
    if (!st.added[id]) opts.action = { label: 'Add for ' + formatMoney(priceNow, cur), onClick: function () { addPick(self, st, id); } };
    try { if (typeof window.clarity === 'function') window.clarity('set', 'hgn_perf_step', 'quickview'); } catch (e) {}
    qv.open(o.h, opts);
  }
  /* v7: the essential — full price, tagged for HQ. */
  function addEssential(self, st, pid) {
    var e = null;
    (st.essentials || []).forEach(function (x) { if (String(x.p) === String(pid)) e = x; });
    if (!e || st.essAdded[e.p]) return;
    st.essAdded[e.p] = 'busy';
    st.dirty = true;
    self._perfReassertStep();
    silentUntil = st.surface === 'cart' ? Date.now() + 4000 : 0;
    st.pending = fetch('/cart/add.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(e.v, 10), quantity: 1, properties: { _hgn_src: 'perf_essential:' + e.sku } }] })
    }).then(function (r) {
      if (!r.ok) throw new Error('add ' + r.status);
      st.essAdded[e.p] = true;
      try { if (typeof window.clarity === 'function') window.clarity('set', 'hgn_perf_essential', e.sku); } catch (x) {}
      return self.refresh ? self.refresh() : null;
    }).catch(function (err) {
      delete st.essAdded[e.p];
      log('essential add failed', { sku: e.sku, err: String(err && err.message || err) });
    }).then(function () {
      st.dirty = true;
      if (self._perfStep === st) self._perfReassertStep();
    });
  }
  function openEssentialView(self, st, pid) {
    var e = null;
    (st.essentials || []).forEach(function (x) { if (String(x.p) === String(pid)) e = x; });
    var qv = window.HGN && window.HGN.quickView;
    if (!e || !e.h || !qv || typeof qv.open !== 'function') return;
    var opts = { context: 'perf-essential' };
    if (!st.essAdded[e.p]) opts.action = { label: 'Add for ' + formatMoney(e.pres, self.cart.currency), onClick: function () { addEssential(self, st, e.p); } };
    qv.open(e.h, opts);
  }
  function addPick(self, st, id) {
    var o = null;
    st.offers.forEach(function (x) { if (x.id === id) o = x; });
    if (!o || st.added[id]) return;
    st.added[id] = 'busy';
    st.dirty = true;
    self._perfReassertStep();
    silentUntil = st.surface === 'cart' ? Date.now() + 4000 : 0;
    st.pending = fetch('/cart/add.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(o.v, 10), quantity: 1, properties: { _hgn_src: 'perf_step:' + o.r + ':' + o.d + ':' + o.id } }] })
    }).then(function (r) {
      if (!r.ok) throw new Error('add ' + r.status);
      st.added[id] = true;
      ev('accept', st.tap, id, st.surface);
      return self.refresh ? self.refresh() : null;
    }).catch(function (err) {
      delete st.added[id];
      ev('add_fail', st.tap, id, st.surface);
      log('add failed', { id: id, err: String(err && err.message || err) });
      st.offers = st.offers.filter(function (x) { return x.id !== id; });
      if (!st.offers.length) { closeStep(self); st.proceed(); }
    }).then(function () {
      st.dirty = true;
      if (self._perfStep === st) self._perfReassertStep();
    });
  }

  /* Cart-page adds must not throw the drawer open over the page. */
  var silentUntil = 0;
  var origOpen = P.open;
  HGNSideCart.prototype.open = function () {
    if (Date.now() < silentUntil && !this.isOpen) return;
    return origOpen.apply(this, arguments);
  };

  /* ── The Checkout tap ── */
  function landing() {
    var l = ssGet(K.land) || {};
    var hrs = function (t) { return t > 0 ? Math.max(0, (Date.now() - t) / 3600000) : null; };
    var gh = null, fh = null;
    if (cookie('_hgn_gclid') || cookie('_hgn_gbraid') || cookie('_hgn_wbraid')) {
      var ca = Date.parse(cookie('_hgn_click_at') || '');
      if (isFinite(ca)) gh = hrs(ca);
    }
    var fbc = cookie('_fbc');
    if (fbc) { var ts = parseInt(fbc.split('.')[2], 10); if (isFinite(ts)) fh = hrs(ts); }
    return { src: l.src || '', med: l.med || '', camp: l.camp || '', ids: l.ids || [], ref: l.ref || '', pv2: !!l.pv2, gh: gh, fh: fh };
  }
  function idsFrom(list) {
    var out = [];
    (list || []).forEach(function (x) { var id = x && String(x.id || x.productId || ''); if (/^\d+$/.test(id) && out.indexOf(id) < 0) out.push(id); });
    return out;
  }
  function viewedIds() {
    var cid = CFG.customer ? String(CFG.customer) : '';
    var out = [];
    [cid, 'anon'].forEach(function (k) { if (k) idsFrom(lsGet('hgn-viewed:' + k)).forEach(function (id) { if (out.indexOf(id) < 0) out.push(id); }); });
    return out.slice(0, 20);
  }
  function wishIds() {
    var w = (lsGet('hgn_wishlist_temp') || {}).wishlist || {};
    var out = [];
    Object.keys(w).forEach(function (k) { idsFrom(w[k]).forEach(function (id) { if (out.indexOf(id) < 0) out.push(id); }); });
    return out.slice(0, 20);
  }
  function popupHeld() {
    try {
      var held = window.HGNPopup && typeof window.HGNPopup.activeCode === 'function' ? window.HGNPopup.activeCode() : null;
      return !!(held && held.code && String(held.code).indexOf('POPUP-') === 0);
    } catch (e) { return false; }
  }
  /* v8 (audit P2-7): a decision lasts 24 h — Shopify keeps the cart token
     through an emptied basket, so "once per token" hid the step for days. */
  var DECIDED_MS = 24 * 3600 * 1000;
  function decidedFor(token) {
    var m = lsGet(K.taps) || {};
    var d = token && m[token];
    return d && Date.now() - (d.at || 0) < DECIDED_MS ? d : null;
  }
  function markDecided(token, ans) {
    if (!token) return;
    var m = lsGet(K.taps) || {}, keep = {}, cutoff = Date.now() - 14 * 86400000;
    Object.keys(m).forEach(function (k) { if (m[k] && m[k].at > cutoff) keep[k] = m[k]; });
    keep[token] = { at: Date.now(), arm: ans.arm || null, tap: ans.tap || null };
    lsSet(K.taps, keep);
  }

  function browserTz() {
    try { return String(Intl.DateTimeFormat().resolvedOptions().timeZone || '').slice(0, 64); } catch (e) { return ''; }
  }
  /* v4 (2 Oct): the free-shipping line in the cart's currency, exactly as the
     side cart's meter has it (HGN_SIDE_CART_CONFIG.shipping, converted from
     GBP by the side cart itself), so the worker can put a pick that clears it
     first and never lead with one that leaves the basket pennies short
     (BUILD.md §9.17). 0 when unknown: the worker then uses the market's
     default, or no shipping rule. */
  function shipLine(self) {
    try {
      var ctx = self._attachContext && self._attachContext();
      var c = ctx && Math.round(Number(ctx.shipCents));
      return c > 0 ? c : 0;
    } catch (e) { return 0; }
  }
  function requestBody(self) {
    var cart = self.cart;
    return {
      v: 1, consent: true, did: deviceId(), cid: cookie('_hgn_cid') || null,
      country: CFG.country || '', theme: String(CFG.theme || ''), cur: cart.currency, tok: cart.token,
      lines: cart.items.slice(0, 60).map(function (it) {
        return { p: String(it.product_id), v: String(it.variant_id), q: it.quantity, u: unitRetail(it),
          k: isBundleLine(it), g: isGiftLineItem(it), o: offerKind(it) };
      }),
      sub: typeof self._lastDisplayedSubtotalCents === 'number' ? self._lastDisplayedSubtotalCents : cart.items_subtotal_price,
      raw: cart.items_subtotal_price,
      ship: shipLine(self),
      intent: (self._getDiscountIntent && self._getDiscountIntent()) || '',
      popup: popupHeld(),
      ref: !!(self._referralClaim && self._referralClaim()),
      ret: !!CFG.ret,
      cust: CFG.customer ? String(CFG.customer) : '',
      ch: landing(),
      viewed: viewedIds(), wish: wishIds(),
      terms: (ssGet('hgn:perf:terms') || []).slice(0, 5), recent: (ssGet('hgn:perf:filters') || []).slice(0, 10),
      declined: declined().concat(self._pairOfferOpen ? pairShown : []),
      held: readOffers().map(signedPart).slice(0, 8),
      tz: browserTz()
    };
  }

  /* Why this tap goes straight through without asking the worker. */
  function localSkip(self) {
    var cart = self.cart;
    if (!cart || !cart.item_count) return 'empty basket';
    if (consentState() === 'no') return 'no consent';
    if (!deviceId()) return 'no device id';
    if (suspended(cart)) return 'discount intent';
    if (decidedFor(cart.token)) return 'already decided for this basket';
    if (cart.attributes && cart.attributes._hgn_kit5_token) return 'KIT5 held';   /* v8 (audit P2-6) */
    /* v10: the worker's own kit rule (decide.js: any line with k), answered here */
    if (!(CFG.kitBaskets === true || window.HGN_PERF_KIT_BASKETS === true) && (cart.items || []).some(isBundleLine)) return 'kit basket';
    return null;
  }

  var origProceed = P._proceedToCheckout;
  HGNSideCart.prototype._proceedToCheckout = function (opts) {
    var self = this, args = arguments;
    /* Re-entries from the original flow (code waits, kit5 gate, the
       acknowledged full-price route) and our own hand-back pass through. */
    if (opts && (opts.__perf || opts.skipMint || opts._applyWaited || opts._welcomeRepaired || opts.kit5Ensured)) return origProceed.apply(this, args);
    if (this._checkoutInflight) return origProceed.apply(this, args);
    if (this._perfAsking) return;
    if (this._perfStep && this._perfStep.open) return;
    var go = function () {
      return Promise.resolve(bounded(syncOffersAttr(self), 1500)).then(function () { return origProceed.call(self, { __perf: 1 }); });
    };
    var skip = localSkip(this);
    if (skip) { DBG.state = 'skip: ' + skip; log('straight through: ' + skip); return go(); }
    var surface = (!this.isOpen && document.querySelector('[data-hgn-cart-page-checkout]')) ? 'cart' : 'drawer';
    this._perfAsking = true;
    var label = this.checkoutLabelEl, prevLabel = label ? label.textContent : null;
    if (label && surface === 'drawer') label.textContent = 'One moment…';
    var t0 = Date.now();
    var body;
    try { body = requestBody(this); } catch (e) { this._perfAsking = false; return go(); }
    DBG.state = 'asking';
    return fetchJson(ENDPOINT + '/v1/offer', body, TIMEOUT_MS).then(function (ans) {
      self._perfAsking = false;
      if (label && surface === 'drawer' && label.textContent === 'One moment…') label.textContent = prevLabel;
      DBG.last = ans; DBG.rtt = Date.now() - t0;
      if (!ans || !ans.ok) { DBG.state = 'no answer'; log('no answer in ' + TIMEOUT_MS + ' ms: straight through'); return go(); }
      log('answer: ' + ans.reason, { arm: ans.arm, step: ans.step, rtt: DBG.rtt });
      if (Number(ans.max_live) >= 1) MAX_LIVE = Math.round(Number(ans.max_live));
      var token = self.cart && self.cart.token;
      var writes = Promise.resolve();
      if (ans.arm || ans.decided) markDecided(token, ans);
      if (ans.arm && !ans.decided) writes = bounded(cartUpdate({ _hgn_arm_offer: ans.arm, _hgn_tap: ans.tap }), 2000);
      ans.offers = ans.offers || [];
      ans.essentials = ans.essentials || [];
      if (!ans.step || (!ans.offers.length && !ans.essentials.length)) { DBG.state = 'straight through'; return writes.then(go); }
      ans.offers.forEach(trust);
      storeOffers(ans.offers);
      /* v6: the free-shipping pick (fs) leads, then the add-on prices, then
         the normal-price picks (the learner's 0% arm); stable otherwise. */
      var rankOf = function (o) { return o.fs ? 0 : o.d > 0 ? 1 : 2; };
      ans.offers = ans.offers.map(function (o, i) { return { o: o, i: i }; })
        .sort(function (a, b) { return rankOf(a.o) - rankOf(b.o) || a.i - b.i; }).map(function (x) { return x.o; });
      return writes.then(function () {
        DBG.state = 'step';
        self._perfStep = { open: true, surface: surface, tap: ans.tap, offers: ans.offers, essentials: ans.essentials, essAdded: {}, added: {}, dirty: true, el: null, pending: null,
          proceed: function () { DBG.state = 'continued'; origProceed.call(self, { __perf: 1 }); } };
        self._perfReassertStep();
        ev('shown', ans.tap, null, surface);
        if (ans.essentials.length) try { if (typeof window.clarity === 'function') window.clarity('set', 'hgn_perf_essential_shown', ans.essentials.map(function (x) { return x.sku; }).join(',')); } catch (e) {}
        try { if (typeof window.clarity === 'function') window.clarity('set', 'hgn_perf_step', 'shown'); } catch (e) {}
      });
    });
  };


  /* ── The add-on price on collection cards and the PDP (scope §6.3) ──
     Only for offers this device holds (issued to it by the step, 48 h
     hold), verified, unexpired, depth > 0, in the page's currency, and not
     already folding on a basket line. Visible price only: never JSON-LD,
     microdata, og:price or product:price. */
  var BOT_UA = /Googlebot|Storebot-Google|AdsBot-Google|Google-InspectionTool|GoogleOther|Mediapartners-Google|bingbot|\b(bot|crawler|spider)\b/i;
  var isBot = BOT_UA.test(navigator.userAgent || '');
  function pageCurrency() {
    var c = mounted && mounted.cart;
    if (c && c.item_count && c.currency) return c.currency;
    try { var raw = window.HGN && window.HGN.cartCurrencyRaw; if (raw) return String(raw).toUpperCase(); } catch (e) {}
    try { return String(window.Shopify.currency.active || '').toUpperCase(); } catch (e) { return ''; }
  }
  function parseMoney(t) {
    var s = String(t == null ? '' : t).replace(/[^\d.,]/g, '');
    if (!s) return 0;
    if (/,\d{2}$/.test(s)) s = s.replace(/\./g, '').replace(',', '.'); else s = s.replace(/,/g, '');
    var n = Math.round(parseFloat(s) * 100);
    return isFinite(n) && n > 0 ? n : 0;
  }
  /* Offers to paint, by product id. */
  function paintable() {
    var out = {};
    if (isBot || consentState() === 'no') return out;
    var cart = mounted && mounted.cart;
    if (cart && suspended(cart)) return out;
    var inBasket = {};
    if (cart) liveOffers(cart).forEach(function (x) { inBasket[x.o.id] = true; });
    if (cart && Object.keys(inBasket).length >= MAX_LIVE) return out;   /* v6: the basket has its add-on prices */
    var cur = pageCurrency(), now = nowSec(), did = deviceId();
    readOffers().forEach(function (o) {
      if (!(o.d > 0) || o.exp <= now || !isTrusted(o) || inBasket[o.id] || o.did !== did) return;
      if (cur && o.cur !== cur) return;
      if (!out[o.p] || out[o.p].exp < o.exp) out[o.p] = o;
    });
    return out;
  }
  function priceLine(normal, offer, cur) {
    return '<s>' + formatMoney(normal, cur) + '</s> <strong>' + formatMoney(offer, cur) + '</strong>';
  }
  function leadText(offer, cur) { return 'Add to your basket for ' + formatMoney(offer, cur); }
  function paintCards(map) {
    var cards = document.querySelectorAll('.hgn-card');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var slot = card.querySelector('.hgn-card__price');
      if (!slot) continue;
      var idEl = card.querySelector('[data-product-id]');
      var pid = idEl ? String(idEl.getAttribute('data-product-id')) : '';
      var o = pid ? map[pid] : null;
      var mine = slot.querySelector('[data-hgn-perf-card]');
      var base = slot.querySelector('.price');
      if (!o) {
        if (mine) { mine.parentNode.removeChild(mine); if (base) base.style.display = ''; }
        continue;
      }
      var cur = o.cur;
      var normal = parseMoney(idEl.getAttribute('data-product-price')) || o.pres;
      var offer = normal - savingFor(normal, o.d);
      var sig = o.id + ':' + normal + ':' + o.exp;
      if (mine && mine.getAttribute('data-hgn-perf-card') === sig) continue;
      if (!mine) { mine = document.createElement('div'); mine.className = 'hgn-perf-card'; slot.appendChild(mine); }
      mine.setAttribute('data-hgn-perf-card', sig);
      mine.innerHTML = '<span class="hgn-perf-card__price"><span class="visually-hidden">Normal price </span>' + priceLine(normal, offer, cur).replace('</s> <strong>', '</s> <span class="visually-hidden">Your add-on price </span><strong>') + '</span>' +
        '<span class="hgn-perf-card__note"' + holdAttrs('card', leadText(offer, cur), o.exp) + '>' + escapeHtml(holdText('card', leadText(offer, cur), o.exp)) + '</span>';
      if (base) base.style.display = 'none';
    }
  }
  function pdpProduct() {
    var pid = '', vid = '';
    try { pid = String(window.ShopifyAnalytics.meta.product.id || ''); } catch (e) {}
    try { var inp = document.querySelector('product-form input[name="id"], form[action*="/cart/add"] input[name="id"]'); vid = inp ? String(inp.value || '') : ''; } catch (e) {}
    return { pid: pid, vid: vid };
  }
  var pdpObs = null;
  function paintPdp(map) {
    var root = document.querySelector('[data-hgn-price]');
    if (!root) return;
    var pr = pdpProduct();
    var o = pr.pid ? map[pr.pid] : null;
    if (o && pr.vid && String(o.v) !== pr.vid) o = null;
    var current = root.querySelector('[data-hgn-price-current]');
    var compare = root.querySelector('[data-hgn-price-compare]');
    var badge = root.querySelector('[data-hgn-price-badge]');
    var note = document.querySelector('[data-hgn-perf-pdp]');
    var painted = root.getAttribute('data-hgn-perf-painted');
    if (!o) {
      if (painted) {
        /* restore exactly what hgn-price had painted */
        var was = {}; try { was = JSON.parse(root.getAttribute('data-hgn-perf-was') || '{}'); } catch (e) {}
        root.removeAttribute('data-hgn-perf-painted');
        if (current) current.textContent = was.cur || current.textContent;
        if (compare) { compare.textContent = was.cmp || ''; compare.style.display = was.cmpShow ? '' : 'none'; }
        if (badge) { badge.textContent = was.badge || 'Sale'; badge.style.display = was.badgeShow ? '' : 'none'; }
        root.classList.toggle('hgn-price--sale', !!was.sale);
      }
      if (note) note.parentNode.removeChild(note);
      return;
    }
    if (!painted) {
      root.setAttribute('data-hgn-perf-was', JSON.stringify({
        cur: current ? current.textContent : '', cmp: compare ? compare.textContent : '', cmpShow: !!(compare && compare.style.display !== 'none'),
        badge: badge ? badge.textContent : '', badgeShow: !!(badge && badge.style.display !== 'none'), sale: root.classList.contains('hgn-price--sale')
      }));
    }
    var was0 = {}; try { was0 = JSON.parse(root.getAttribute('data-hgn-perf-was') || '{}'); } catch (e) {}
    var cur = o.cur;
    /* struck through against the CURRENT price, never a compare-at price */
    var normal = parseMoney(was0.cur) || o.pres;
    var offer = normal - savingFor(normal, o.d);
    var sig = o.id + ':' + normal + ':' + o.exp;
    root.setAttribute('data-hgn-perf-painted', sig);
    var want = formatMoney(offer, cur);
    if (current && current.textContent !== want) current.textContent = want;
    if (compare) { var n = formatMoney(normal, cur); if (compare.textContent !== n) compare.textContent = n; if (compare.style.display) compare.style.display = ''; }
    if (badge) { if (badge.textContent !== 'Add-on price') badge.textContent = 'Add-on price'; if (badge.style.display) badge.style.display = ''; }
    if (!root.classList.contains('hgn-price--sale')) root.classList.add('hgn-price--sale');
    var lead = leadText(offer, cur);
    var text = holdText('pdp', lead, o.exp);
    if (!note) {
      note = document.createElement('p');
      note.className = 'hgn-perf-pdp-note';
      note.setAttribute('data-hgn-perf-pdp', '');
      root.parentNode.insertBefore(note, root.nextSibling);
    }
    if (note.getAttribute('data-hgn-perf-exp') !== String(o.exp)) note.setAttribute('data-hgn-perf-exp', String(o.exp));
    if (note.getAttribute('data-hgn-perf-kind') !== 'pdp') note.setAttribute('data-hgn-perf-kind', 'pdp');
    if (note.getAttribute('data-hgn-perf-lead') !== lead) note.setAttribute('data-hgn-perf-lead', lead);
    if (note.textContent !== text) note.textContent = text;
    /* hgn-price repaints itself on variant events: put ours back after */
    if (!pdpObs && typeof MutationObserver === 'function') {
      pdpObs = new MutationObserver(function () { schedulePaint(); });
      pdpObs.observe(root, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class', 'style'] });
    }
  }
  var paintQueued = false, bodyObs = null, lastVerifyKey = '';
  function paintNow() {
    paintQueued = false;
    if (isBot) return;
    var stored = readOffers().filter(function (o) { return o.d > 0 && o.did === deviceId(); });
    if (!stored.length) {
      paintCards({}); paintPdp({});
      if (bodyObs) { bodyObs.disconnect(); bodyObs = null; }
      return;
    }
    /* offers on this page not yet trusted: ask the worker once */
    var onPage = {};
    var ids = document.querySelectorAll('.hgn-card [data-product-id]');
    for (var i = 0; i < ids.length; i++) onPage[String(ids[i].getAttribute('data-product-id'))] = true;
    var pr = pdpProduct(); if (pr.pid) onPage[pr.pid] = true;
    var unverified = stored.filter(function (o) { return onPage[o.p] && !isTrusted(o); });
    var vkey = unverified.map(function (o) { return o.id; }).join(',');
    if (unverified.length && vkey !== lastVerifyKey) {
      lastVerifyKey = vkey;
      verifyList(unverified).then(function () { schedulePaint(); });
    }
    var map = paintable();
    paintCards(map); paintPdp(map);
    DBG.painted = Object.keys(map);
    if (!bodyObs && typeof MutationObserver === 'function' && document.body) {
      bodyObs = new MutationObserver(function (muts) {
        for (var j = 0; j < muts.length; j++) {
          var t = muts[j].target;
          if (t && t.closest && (t.closest('[data-hgn-perf-card]') || t.closest('[data-hgn-price]') || t.closest('[data-hgn-perf-pdp]') || t.closest('[data-hgn-perf-step]'))) continue;
          schedulePaint(); return;
        }
      });
      bodyObs.observe(document.body, { childList: true, subtree: true });
    }
    /* the end-time lines just painted: the ticker keeps them true and takes
       the paint off at the real end */
    tick();
  }
  function schedulePaint() {
    if (paintQueued) return;
    paintQueued = true;
    (window.requestAnimationFrame || setTimeout)(function () { try { paintNow(); } catch (e) { paintQueued = false; log('paint error: ' + (e && e.message)); } });
  }
  ['hgn:grid:augmented', 'hgn:perf:ranked', 'variant:change'].forEach(function (n) { document.addEventListener(n, schedulePaint); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedulePaint); else schedulePaint();
  DBG.paint = paintNow;

  /* ── The one shared timer (v3) ──
     Rewrites every end-time line on screen ([data-hgn-perf-exp]) and wakes
     when the next one changes: at the 24 h mark, then at each minute. At a
     line's real end it runs the lapse path once: the cards and PDP repaint
     (paint off), the drawer re-renders (the fold drops, the "ended at" note
     shows, the step's picks go back to their normal price). No line on
     screen, or the tab hidden: no timer. */
  var tickTimer = null, endedSeen = {};
  function shown(el) { try { return el.getClientRects().length > 0; } catch (e) { return true; } }
  function tick() {
    if (tickTimer) { clearTimeout(tickTimer); tickTimer = null; }
    DBG.ticking = false;
    if (document.hidden) return;
    var els = document.querySelectorAll('[data-hgn-perf-exp]');
    var nowMs = Date.now(), wait = Infinity, ended = false;
    for (var i = 0; i < els.length; i++) {
      var el = els[i], exp = Number(el.getAttribute('data-hgn-perf-exp'));
      if (!(exp > 0)) continue;
      var leftMs = exp * 1000 - nowMs;
      if (leftMs <= 0) { if (!endedSeen[exp]) { endedSeen[exp] = true; ended = true; } continue; }
      var t = holdText(el.getAttribute('data-hgn-perf-kind') || '', el.getAttribute('data-hgn-perf-lead') || '', exp);
      if (el.textContent !== t) el.textContent = t;
      /* on screen: wake at the 24 h mark, then each minute (≤ 15 min, so a
         device that slept catches up). In the page but not visible (a hidden
         card, a closed drawer): no ticking, one wake at the real end only, to
         take the paint off as v2 did; it is brought up to date when shown
         (drawer open, tab back, repaint). */
      var w = !shown(el) ? leftMs : Math.min(leftMs > DAY_S * 1000 ? leftMs - DAY_S * 1000 : (leftMs % 60000 || 60000), 15 * 60000);
      if (w < wait) wait = w;
    }
    if (isFinite(wait)) {
      tickTimer = setTimeout(tick, Math.min(Math.max(wait + 40, 250), 2147483000));
      DBG.ticking = true;
    }
    if (ended) {
      log('hold ended: lapse path');
      schedulePaint();
      var self = mounted;
      if (self && self.cart) {
        if (self._perfStep) self._perfStep.dirty = true;
        try { self._render(); } catch (e) { log('lapse render error: ' + (e && e.message)); }
      }
    }
  }
  /* soon, through the same single timer (after a show, a repaint, a wake) */
  function kick() {
    if (tickTimer) clearTimeout(tickTimer);
    tickTimer = setTimeout(tick, 30);
  }
  DBG.tick = tick;
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) { if (tickTimer) clearTimeout(tickTimer); tickTimer = null; DBG.ticking = false; } else kick();
  });
  var origOpen2 = P.open;
  HGNSideCart.prototype.open = function () { var r = origOpen2.apply(this, arguments); kick(); return r; };

  /* bfcache restore: in-flight flags froze with the page. */
  try { window.addEventListener('pageshow', function (e) { if (e && e.persisted) { if (mounted) { mounted._perfAsking = false; closeStep(mounted); } kick(); } }); } catch (e) {}
  log('loaded');
})();
